import {
    ChangeDetectorRef,
    Component,
    OnInit,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { ButtonComponent } from "@components/button/button.component";
import { BlockComponent } from "@components/container-block/block.component";
import { MenuButtonComponent } from "@components/menu-button/menu-button.component";
import { PageComponent } from "@components/page/page.component";
import { SaveButtonComponent } from "@components/save-button/save-button.component";
import { TabComponent } from "@components/tabs/tab/tab.component";
import { TabsComponent } from "@components/tabs/tabs.component";
import { combineLatest } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { IOption } from "~app/common/models/options-models";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { DirtyFormService, IDirtyFormResult } from "~app/common/services/dirty-form.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { RaskTitleService } from "~app/common/services/title.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { IValidationScope } from "~app/common/validation/IValidationScope";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { FormMode } from "~app/features/forsaljning/produkt/produkt-detail/produkt-details.models";
import { IProduktinstruktion } from "../models/IProduktdefinition";
import { IProduktdefinitionBasdata } from "../models/ProduktdefinitionModels";
import { ProduktdefinitionViewModel } from "../models/ProduktdefinitionViewModel";
import { ProduktinstruktionerHistorikComponent } from "../produktinstruktioner-historik/produktinstruktioner-historik.component";
import { ProduktinstruktionService } from "../services/produktinstruktion.service";
import { ProduktinstruktionerDetailsHeaderComponent } from "./produktinstruktioner-details-header/produktinstruktioner-details-header.component";
import { ProduktinstruktionerValidation } from "./produktinstruktioner.validation";
import { BegransningsmatrisComponent } from "./tabs/begransningsmatris/begransningsmatris.component";
import { FargmarkningComponent } from "./tabs/fargmarkning/fargmarkning.component";
import { FordelningsmatrisComponent } from "./tabs/fordelningsmatris/fordelningsmatris.component";
import { GrunddataComponent } from "./tabs/grunddata/grunddata.component";
import { RestriktionerComponent } from "./tabs/restriktioner/restriktioner.component";
import { StolpComponent } from "./tabs/stolp/stolp.component";
import { VardematrisComponent } from "./tabs/vardematris/vardematris.component";

@Component({
    selector: "rask-produktinstruktioner-details",
    templateUrl: "./produktinstruktioner-details.component.html",
    styleUrls: ["./produktinstruktioner-details.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        PageComponent,
        ActionButtonComponent,
        MenuButtonComponent,
        ProduktinstruktionerDetailsHeaderComponent,
        BlockComponent,
        TabsComponent,
        TabComponent,
        GrunddataComponent,
        VardematrisComponent,
        FordelningsmatrisComponent,
        BegransningsmatrisComponent,
        FargmarkningComponent,
        StolpComponent,
        RestriktionerComponent,
        ButtonComponent,
        SaveButtonComponent
    ]
})
export class ProduktinstruktionerDetailsComponent implements OnInit {
    formMode = FormMode.NotSet;
    form = new UntypedFormGroup({});
    selectedProduktdefinition: ProduktdefinitionViewModel;
    copyOptions: IOption[];
    init = false;
    puid: string;
    historikId?: number;
    produktinstruktionBasdata: IProduktdefinitionBasdata;
    produktinstruktion: IProduktinstruktion;
    validationContext: IValidationContext;
    validation: IValidationScope;

    @ViewChild("vardematris") vardematris: VardematrisComponent;
    @ViewChild("fordelningsmatris") fordelningsmatris: FordelningsmatrisComponent;

    get title(): string {
        if (this.formMode === FormMode.New) {
            return "Skapa ny produktinstruktion";
        }

        const basis = `${this.selectedProduktdefinition?.headerData?.namn} (${this.puid})`;
        if (this.isUtkastSelected()) {
            return `${basis} - Utkast`;
        } else if (this.selectedProduktdefinition.isHistorisk) {
            return `${basis} - Historisk version`;
        } else {
            return basis;
        }
    }

    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly produktinstruktionService = inject(ProduktinstruktionService);
    private readonly busyService = inject(BusyService);
    private readonly cdRef = inject(ChangeDetectorRef);
    private readonly raskTitleService = inject(RaskTitleService);
    private readonly dirtyFormService = inject(DirtyFormService);
    private readonly raskModalService = inject(RaskModalService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly formBinder = inject(FormBinderService);

    ngOnInit() {

        const puidChange$ = this.route.params.pipe(
            map(params => params.puid),
            tap(puid => {
                this.puid = puid;
                if (puid === "ny") {
                    this.formMode = FormMode.New;
                    this.form.markAsDirty();
                }
            }),
            switchMap(puid => this.produktinstruktionService.getProduktinstruktionDetails(puid))
        );

        combineLatest([
            puidChange$,
            this.produktinstruktionService.getProduktinstruktionBasData(),
            this.produktinstruktionService.getHandelssortiment()
        ]).subscribe((result) => {
            const [
                data,
                basData,
                handelssortiment
            ] = result;
            this.produktinstruktionBasdata = basData;
            this.setFetched(data);
            this.updateTitle();
            this.produktinstruktionBasdata.handelssortiment = handelssortiment.filter(x => x.forProduktion ||
                x.violId === this.selectedProduktdefinition.headerData.handelssortiment);

            this.dirtyFormService.onDirty(this.form,
                () => this.save(),
                d => this.dirtyCheckCallback(d));
            this.validationContext = ProduktinstruktionerValidation.init(this.selectedProduktdefinition);
            this.validationContext.scope(ValidationScopes.spara).subscribe(v => this.validation = v);
            this.formBinder.attachValidation(this.validationContext.current, this.form);
            this.cdRef.detectChanges();
            this.handleFormState();
            this.route.queryParams.subscribe(queryParams => {
                if (+queryParams?.historikId !== this.historikId) {
                    this.historikId = +queryParams?.historikId;
                    if (this.historikId) {
                        this.loadHistorik(this.historikId);
                    }
                }
            });
        });
        this.form.valueChanges.subscribe(x => {
            setTimeout(() => {
                this.formBinder.updateErrors(this.validation, this.form);
            }, 200);
        });
    }

    loadHistorik(id: number) {
        this.busyService.addWork(this.produktinstruktionService.getHistoriskProduktinstruktionDetails(this.produktinstruktion.puid, id)).subscribe(y => {
            this.selectedProduktdefinition = new ProduktdefinitionViewModel(this.produktinstruktion, this.form, false, this.canEdit(), this.produktinstruktionBasdata);
            this.selectedProduktdefinition.selectHistorik(y);
            this.updateTitle();
            this.handleFormState();
        });
    }

    save() {
        return new Promise((resolve, reject) => {
            this.validationContext.validate();
            if (!this.validationContext.hasErrors(ValidationScopes.spara)) {
                if (this.formMode === FormMode.New) {
                    // eslint-disable-next-line sonarjs/deprecation
                    this.busyService.addWork(this.produktinstruktionService.createProduktinstruktion(this.selectedProduktdefinition.getDto())).subscribe(data => {
                        this.form.markAsPristine();
                        this.routeToPuid(data.puid, true);
                        resolve(true);
                    }, (err) => {
                        reject(err);
                    });
                } else {
                    this.busyService.addWork(this.produktinstruktionService.saveProduktinstruktion(this.selectedProduktdefinition.getDto())).subscribe({
                        next: data => {
                            this.produktinstruktion.utkastProduktdefinition = data;
                            this.selectedProduktdefinition = new ProduktdefinitionViewModel(this.produktinstruktion,
                                this.form,
                                true,
                                this.canEdit(),
                                this.produktinstruktionBasdata);
                            this.form.markAsPristine();
                            resolve(true);
                        },
                        error: (err) => {
                            reject(err);
                        }
                    });
                }
            }
        });
    }

    canEdit(): boolean {
        return this.authenticationService.canEdit();
    }

    canSave(): boolean {
        return this.form.dirty && this.form.valid;
    }

    downloadPin() {
        const downloadAction = (viol3: boolean) => {
            if (this.selectedProduktdefinition.isHistorisk) {
                this.produktinstruktionService.downloadPinHistorisk(this.puid,
                    this.selectedProduktdefinition.headerData.tradslagsgrupp.namn,
                    this.selectedProduktdefinition.headerData.namn,
                    this.selectedProduktdefinition.headerData.id,
                    viol3);
            } else {
                this.produktinstruktionService.downloadPin(
                    this.puid,
                    this.selectedProduktdefinition.headerData.tradslagsgrupp.namn,
                    this.selectedProduktdefinition.headerData.namn,
                    viol3,
                    this.selectedProduktdefinition.isUtkast);
            }
        };
        if (this.selectedProduktdefinition.headerData.handelssortiment) {
            downloadAction(true);
        }
        if (this.selectedProduktdefinition.headerData.sortiment) {
            downloadAction(false);
        }
    }

    hamtaUtkast() {
        this.updateUrl();
        this.selectedProduktdefinition = new ProduktdefinitionViewModel(this.produktinstruktion, this.form, true, this.canEdit(), this.produktinstruktionBasdata);
        this.validationContext = ProduktinstruktionerValidation.init(this.selectedProduktdefinition);
        this.updateTitle();
        this.handleFormState();
    }

    skapaUtkast() {
        const skapaUtkastAction = () => {
            const id = this.selectedProduktdefinition.isHistorisk ? this.selectedProduktdefinition.headerData.id : undefined;
            this.busyService.addWork(this.produktinstruktionService.checkoutProduktdefinition(this.puid, id))
                .subscribe(x => {
                    this.updateUrl();
                    this.produktinstruktion.utkastProduktdefinition = x;
                    this.selectedProduktdefinition = new ProduktdefinitionViewModel(this.produktinstruktion, this.form, true, this.canEdit(), this.produktinstruktionBasdata);
                    this.updateTitle();
                    this.handleFormState();
                });
        };

        if (this.selectedProduktdefinition.isHistorisk) {
            this.raskModalService.confirm(new ModalSettings("Skapa utkast från tidigare version",
                "Du håller på att skapa utkast från en tidigare version. Det kommer att skriva över eventuellt existerande utkast. Vill du fortsätta ändå?"))
                .subscribe((result) => {
                    if (result === ConfirmDialogResponse.save) {
                        skapaUtkastAction();
                    }

                });
        } else {
            skapaUtkastAction();
        }
    }

    updateUrl(historikId?: number) {
        this.router.navigate([this.puid], {
            queryParams: {
                historikId
            },
            replaceUrl: true,
            relativeTo: this.route.parent
        });
    }

    dirtyCheckCallback(result: IDirtyFormResult) {
        if (result.isSkipSave) {
            return;
        } else if (result.isCancel) {
            return;
        } else {
            this.save();
        }
    }

    visaHistorik() {
        this.produktinstruktionService.getHistorik(this.produktinstruktion.puid).subscribe(dto => {
            const data = {
                historik: dto.historik,
                puid: dto.puid
            };
            this.raskModalService.open("Historik", ProduktinstruktionerHistorikComponent, "xl", true, data);
        });

    }

    publicera = () => {
        const publiceraAction = () => {
            this.busyService.addWork(this.produktinstruktionService.publiceraProduktdefinition(this.selectedProduktdefinition.headerData.puid)
                .pipe(switchMap(_ => this.produktinstruktionService.getProduktinstruktionDetails(this.puid))))
                .subscribe(prod => {
                    this.setFetched(prod);
                    this.handleFormState();
                });
        };

        this.checkDirtyModal(() => {
            if (this.produktinstruktion.aktivProduktdefinition &&
                this.produktinstruktion.aktivProduktdefinition.version === this.produktinstruktion.utkastProduktdefinition.version) {
                this.raskModalService.confirm(new ModalSettings("Validera publicering",
                    `Versionsnummer "${this.produktinstruktion.aktivProduktdefinition.version}" är redan publicerat. Vill du fortsätta ändå?`))
                    .subscribe((result) => {
                        if (result === ConfirmDialogResponse.save) {
                            publiceraAction();
                        }
                    });
            } else {
                publiceraAction();
            }
        });
    };

    discardDraft = () => {
        this.raskModalService.confirm(new ModalSettings("Ångra utkast", `Är du säker på att du vill ångra utkastet?`))
            .subscribe((result) => {
                if (result === ConfirmDialogResponse.save) {
                    this.busyService.addWork(this.produktinstruktionService.discardProduktdefinition(this.selectedProduktdefinition.headerData.puid))
                        .subscribe(() => {
                            this.produktinstruktion.utkastProduktdefinition = null;
                            this.form.markAsPristine();
                            this.hamtaPublicerad();
                        });
                }
            });
    };

    checkDirtyModal(onComplete: () => any) {
        this.dirtyFormService.popDirtyDialog(() => new Promise((resolve, reject) => {
            this.produktinstruktionService.getProduktinstruktionDetails(this.puid)
                // eslint-disable-next-line sonarjs/deprecation
                .subscribe(x => {
                    this.setFetched(x);
                    resolve(true);
                }, (err) => reject(err));
        }), () => {
            onComplete();
        });
    }

    hamtaPublicerad = () => {
        this.checkDirtyModal(() => {
            this.updateUrl();
            this.selectedProduktdefinition = new ProduktdefinitionViewModel(this.produktinstruktion, this.form, false, this.canEdit(), this.produktinstruktionBasdata);
            this.updateTitle();
            this.handleFormState();
        });
    };

    inaktivera() {
        const inaktiveraAction = () => {
            this.busyService.addWork(this.produktinstruktionService.inaktiveraProdktinstruktion(this.selectedProduktdefinition.headerData.puid)).subscribe(x => {
                this.produktinstruktion = x;
                this.selectedProduktdefinition = new ProduktdefinitionViewModel(x, this.form, false, this.canEdit(), this.produktinstruktionBasdata);
            });
        };
        this.raskModalService.confirm(new ModalSettings("Inaktivera",
            `När en produktinstruktion inaktiveras kommer den att tas bort från samtliga arbetsorder där virkesorder inte har skapats. Vill du fortsätta ändå?`))
            .subscribe((result) => {
                if (result === ConfirmDialogResponse.save) {
                    inaktiveraAction();
                }
            });
    }

    aktivera() {
        this.busyService.addWork(this.produktinstruktionService.aktiveraProdktinstruktion(this.selectedProduktdefinition.headerData.puid)).subscribe(x => {
            this.produktinstruktion = x;
            this.selectedProduktdefinition = new ProduktdefinitionViewModel(x, this.form, false, this.canEdit(), this.produktinstruktionBasdata);
        });
    }

    canVisaPublicerad(): boolean {
        return !ObjectUtils.isNullOrUndefined(this.produktinstruktion.aktivProduktdefinition) &&
            this.produktinstruktion.aktivProduktdefinition.id !== this.selectedProduktdefinition.headerData.id;
    }

    canSkapaKopia(): boolean {
        return !ObjectUtils.isNullOrUndefined(this.produktinstruktion.aktivProduktdefinition) &&
            this.produktinstruktion.aktivProduktdefinition.id === this.selectedProduktdefinition.headerData.id
            && !this.selectedProduktdefinition.isHistorisk;
    }

    skapaKopia() {
        this.busyService.addWork(this.produktinstruktionService.kopieraProduktinstruktion(this.selectedProduktdefinition.headerData.puid))
            .subscribe((dto) => {
                this.routeToPuid(dto.puid);
            });
    }

    canPublicera(): boolean {
        return (((!ObjectUtils.isNullOrUndefined(this.produktinstruktion.utkastProduktdefinition))
            && this.produktinstruktion.utkastProduktdefinition?.id === this.selectedProduktdefinition.headerData.id)
            || this.selectedProduktdefinition.isHistorisk) && this.canEdit();
    }

    canDiscardDraft(): boolean {
        return !ObjectUtils.isNullOrUndefined(this.produktinstruktion.utkastProduktdefinition) &&
            this.produktinstruktion.utkastProduktdefinition?.id === this.selectedProduktdefinition.headerData.id &&
            this.canVisaPublicerad() && this.canEdit() && !this.selectedProduktdefinition.isHistorisk;
    }

    canSkapaUtkast(): boolean {
        return (ObjectUtils.isNullOrUndefined(this.produktinstruktion.utkastProduktdefinition) || this.selectedProduktdefinition.isHistorisk) && this.canEdit();
    }

    canVisaUtkast(): boolean {
        return !ObjectUtils.isNullOrUndefined(this.produktinstruktion.utkastProduktdefinition) &&
            !this.isUtkastSelected();
    }

    canInaktivera(): boolean {
        return this.produktinstruktion.aktivProduktdefinition?.id === this.selectedProduktdefinition.headerData.id && this.canEdit() && this.produktinstruktion.aktiv
            && !this.selectedProduktdefinition.isHistorisk;
    }

    canAktivera(): boolean {
        return this.produktinstruktion.aktivProduktdefinition?.id === this.selectedProduktdefinition.headerData.id && this.canEdit() && !this.produktinstruktion.aktiv
            && !this.selectedProduktdefinition.isHistorisk;
    }

    isNew(): boolean {
        return this.formMode === FormMode.New;
    }

    canDelete() {
        return this.selectedProduktdefinition.isUtkast && !this.selectedProduktdefinition.isHistorisk;
    }

    delete() {
        this.busyService.addWork(this.produktinstruktionService.deleteProduktinstruktion(this.produktinstruktion.puid))
            .subscribe(x => {
                this.router.navigate(["/produktinstruktioner"]);
            });
    }

    private routeToPuid(puid: string, replaceUrl: boolean = undefined) {
        // Hack för att ladda om komponenten vid redirect
        this.router.navigateByUrl("/", { skipLocationChange: true }).then(() => {
            this.router.navigate([
                "/produktinstruktioner",
                puid
            ]);
        });
    }

    private isUtkastSelected(): boolean {
        return this.produktinstruktion.utkastProduktdefinition?.id === this.selectedProduktdefinition.headerData.id;
    }

    private setFetched(data: IProduktinstruktion) {
        this.produktinstruktion = data;
        this.selectedProduktdefinition = new ProduktdefinitionViewModel(
            data,
            this.form,
            !ObjectUtils.isNullOrUndefined(data.utkastProduktdefinition),
            this.canEdit(),
            this.produktinstruktionBasdata);

        this.updateTitle();
    }

    private handleFormState(): void {
        if (!this.selectedProduktdefinition.canEdit) {
            this.form.disable();
        } else {
            this.form.enable();
        }

        if (!this.isNew()) {
            this.form.markAsPristine();
        }
    }

    private updateTitle(): void {
        if (this.isNew()) {
            this.raskTitleService.setTitle("Skapa ny produktinstruktion");
        } else {
            this.raskTitleService.setTitle(`${this.isUtkastSelected() ? "*" : ""}${this.selectedProduktdefinition?.headerData?.namn} ${this.puid}`);
        }
    }
}
